import { type FC, useContext } from 'react';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';
import SV from 'server-vars';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft.ts';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton.tsx';
import { GetHelpZendeskArticlesSearch } from './GetHelpZendeskArticlesSearch.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS, STEPS } from './GetHelpZendeskConstants.ts';
import { type Step } from './GetHelpZendeskTypes.ts';

import { type GetHelpZendeskModalHeader_viewer$key } from './__generated__/GetHelpZendeskModalHeader_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskModalHeader.scss';

const isBuyerDesktop = SV.get('settings.isDesktop');
const isSellerDesktop = SV.get('settings.deviceType') === 'desktop';

const GetHelpZendeskModalHeader: FC<{
    viewer: GetHelpZendeskModalHeader_viewer$key;
}> = ({ viewer: viewerRef }) => {
    const {
        state: { onClose, step },
        dispatch,
    } = useContext(GetHelpZendeskContext);

    const viewer = useFragment(
        graphql`
            fragment GetHelpZendeskModalHeader_viewer on Viewer {
                ...GetHelpZendeskArticlesSearch_viewer
            }
        `,
        viewerRef
    );

    const showSearch =
        step &&
        (
            [
                STEPS.ORDER_INFO,
                STEPS.ARTICLE,
                STEPS.SEARCH_RESULTS,
                STEPS.NO_SEARCH_RESULTS,
                STEPS.CMS_TOPIC,
            ] as Step[]
        ).includes(step);

    const isDesktop = isBuyerDesktop || isSellerDesktop;

    return (
        <div
            className={classnames(
                styles.container,
                dibsCss.borderB,
                dibsCss.borderBSolid,
                dibsCss.borderDolphin,
                dibsCss.pxSmall,
                dibsCss.sticky,
                dibsCss.bgWhite,
                dibsCss.top0,
                dibsCss.zBump,
                {
                    [dibsCss.borderB0]: step === STEPS.INTRO,
                    [dibsCss.mbMedium]: step !== STEPS.INTRO,
                    [styles.isDraggable]: isDesktop,
                }
            )}
        >
            <div className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.minH60px)}>
                {step !== STEPS.INTRO && (
                    <>
                        <Link
                            onClick={() => {
                                dispatch({ type: ACTIONS.GO_BACK });
                            }}
                        >
                            <ArrowLeft
                                className={classnames(
                                    dibsCss.hSmall,
                                    dibsCss.wSmall,
                                    dibsCss.fillPitchblack
                                )}
                            />
                        </Link>
                        <span
                            className={classnames(dibsCss.sassyFontBodyLargeHeavy, dibsCss.mAuto)}
                        >
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskModalHeader.title"
                                defaultMessage="Get Help"
                            />
                        </span>
                    </>
                )}
                <ModalCloseButton onClick={() => onClose()} className={styles.closeButton} />
            </div>
            {showSearch && (
                <div className={dibsCss.mbSmall}>
                    <GetHelpZendeskArticlesSearch viewer={viewer} />
                </div>
            )}
        </div>
    );
};
export { GetHelpZendeskModalHeader };
